import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/Badge';
import { formatDate } from '../utils/format';

const DEMO_SURVEYS = [
  {
    id: 's1',
    title: 'Pulso trimestral Q1',
    audience: 'all',
    status: 'active',
    responses: 186,
    score: 4.1,
    closesAt: '2026-03-15',
  },
  {
    id: 's2',
    title: 'eNPS · Cultura',
    audience: 'managers',
    status: 'active',
    responses: 42,
    score: 38,
    closesAt: '2026-02-28',
  },
  {
    id: 's3',
    title: 'Carga tras release',
    audience: 'employees',
    status: 'draft',
    responses: 0,
    score: null,
    closesAt: '2026-04-01',
  },
  {
    id: 's4',
    title: 'Bienestar remoto',
    audience: 'all',
    status: 'closed',
    responses: 210,
    score: 4.4,
    closesAt: '2025-12-20',
  },
];

const PULSE = [
  { key: 'motivation', value: 78 },
  { key: 'workload', value: 62 },
  { key: 'leadership', value: 81 },
  { key: 'communication', value: 74 },
];

const STATUS_MAP = {
  active: 'active',
  draft: 'draft',
  closed: 'approved',
};

export function ClimateSurveys() {
  const { t } = useApp();

  const kpis = useMemo(() => {
    const climateAvg = 4.2;
    const responses = DEMO_SURVEYS.reduce((a, s) => a + s.responses, 0);
    const active = DEMO_SURVEYS.filter((s) => s.status === 'active').length;
    const enps = 42;
    return { climateAvg, responses, active, enps };
  }, []);

  return (
    <div className="page-stack people-suite-page people-suite-page--climate">
      <div className="people-suite-hero">
        <p className="people-suite-hero__eyebrow">{t('climate.eyebrow')}</p>
        <h2 className="people-suite-hero__title">{t('climate.heroTitle')}</h2>
        <p className="people-suite-hero__lead">{t('climate.heroLead')}</p>
        <span className="banner--muted people-suite-hero__banner">{t('climate.demoBanner')}</span>
      </div>

      <div className="people-suite-kpis">
        <StatCard icon="◎" label={t('climate.kpiClimate')} value={kpis.climateAvg} tone="accent" />
        <StatCard icon="✉" label={t('climate.kpiResponses')} value={kpis.responses} />
        <StatCard icon="◈" label={t('climate.kpiActive')} value={kpis.active} tone="warning" />
        <StatCard icon="★" label={t('climate.kpiEnps')} value={kpis.enps} tone="success" />
      </div>

      <section className="panel panel--elevated people-suite-panel people-suite-risks">
        <h3 className="people-suite-panel__title">{t('climate.risksTitle')}</h3>
        <p className="panel__muted">{t('climate.risksBody')}</p>
      </section>

      <div className="people-suite-two-col">
        <section className="panel panel--elevated people-suite-panel">
          <h3 className="people-suite-panel__title">{t('climate.pulseTitle')}</h3>
          <p className="panel__muted">{t('climate.pulseIntro')}</p>
          <ul className="people-suite-pulse">
            {PULSE.map((p) => (
              <li key={p.key} className="people-suite-pulse__row">
                <span>{t(`climate.pulse_${p.key}`)}</span>
                <div className="people-suite-pulse__track">
                  <div className="people-suite-pulse__fill" style={{ width: `${p.value}%` }} />
                </div>
                <span className="people-suite-pulse__value">{p.value}%</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="panel panel--elevated people-suite-panel people-suite-panel--accent">
          <h3 className="people-suite-panel__title">{t('climate.listTitle')}</h3>
          <ul className="people-suite-card-list">
            {DEMO_SURVEYS.map((s) => (
              <li key={s.id} className="people-suite-survey-card">
                <div className="people-suite-survey-card__top">
                  <div>
                    <div className="people-suite-survey-card__title">{s.title}</div>
                    <div className="people-suite-survey-card__meta">
                      {t('climate.audience')}: {t(`climate.aud_${s.audience}`)} · {t('climate.closes')}:{' '}
                      {formatDate(s.closesAt)}
                    </div>
                  </div>
                  <Badge variant={STATUS_MAP[s.status] || 'mutedFallback'}>{t(`climate.status_${s.status}`)}</Badge>
                </div>
                <div className="people-suite-survey-card__bottom">
                  <span>
                    {t('climate.responses')}: <strong>{s.responses}</strong>
                  </span>
                  <span>
                    {t('climate.score')}:{' '}
                    <strong>{s.score != null ? s.score : '—'}</strong>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

    </div>
  );
}
